import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  styled,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  DynamicForm,
  DynamicIcon,
  EllipsesMenu,
  FormEditableText,
  Tooltip,
} from 'src/components';
import { Address, Project, useProject } from 'src/graphql';
import { useCopyToClipboard, useNotifications } from 'src/utils/hooks';
import { useProjectActions } from './use-project-actions';
import {
  formatProjectDate,
  ProjectCardFormValues,
  useProjectCardForm,
} from './use-project-card-form';
import { hasChanges } from './utils';

interface ProjectCardProps {
  address: Address;
  project: Project;
  disabled?: boolean;
}

export const StyledCardHeader = styled(CardHeader, {
  name: 'StyledCardHeader',
})(({ theme }) => ({
  alignSelf: 'center',
  borderBottom: '1px solid',
  borderColor: theme.palette.custom.OV.sys.outlineVariant,
  padding: theme.spacing(1, 2),

  '& .MuiCardHeader-action': {
    alignSelf: 'center',
  },

  '& .MuiCardHeader-content': {
    overflow: 'hidden',
  },
}));

export const ProjectCard = ({ project, address, disabled }: ProjectCardProps) => {
  const theme = useTheme();
  const { showSuccessAlert } = useNotifications();
  const { formFields } = useProjectCardForm({ partnerId: address.partnerId, project });
  const { updateProject } = useProject(project.id);
  const { copyToClipboard } = useCopyToClipboard();
  const form = useForm({
    values: { projectName: project?.name },
  });

  useEffect(() => {
    form.reset({ projectName: project?.name });
  }, [project, form]);

  const onSubmitEditedProjectName = useCallback(
    async (data: { projectName: string }) => {
      const { projectName } = data;

      if (projectName && project?.name !== projectName) {
        const result = await updateProject({
          name: projectName,
          projectStageId: project.projectStageId,
          ownerId: project.ownerId,
          estimatedDay1Date: project.estimatedDay1Date,
          day1StartDate: project.day1StartDate,
          warrantyExpirationDate: project.warrantyExpirationDate,
          signingDate: project.signingDate,
          projectLostReasonId: project.projectLostReasonId,
          lostReasonNotes: project.lostReasonNotes,
        });

        if (!result.errors) {
          showSuccessAlert('Project name updated!');
        }
      }
    },
    [
      project.day1StartDate,
      project.estimatedDay1Date,
      project.lostReasonNotes,
      project?.name,
      project.ownerId,
      project.projectLostReasonId,
      project.projectStageId,
      project.signingDate,
      project.warrantyExpirationDate,
      showSuccessAlert,
      updateProject,
    ],
  );

  const {
    submitDay1,
    proposalWon,
    proposalLost,
    copyProjectId,
    copyProjectUrl,
    deleteProject,
  } = useProjectActions({ project, address });

  const menuOptions = useMemo(
    () =>
      [
        submitDay1,
        proposalWon,
        proposalLost,
        copyProjectId,
        copyProjectUrl,
        deleteProject,
      ].filter((option) => ('isAllowed' in option ? option.isAllowed : true)),
    [submitDay1, proposalWon, proposalLost, copyProjectId, copyProjectUrl, deleteProject],
  );

  const handleNameCopy = useCallback(async () => {
    await copyToClipboard(project.name);
  }, [copyToClipboard, project.name]);

  const onSubmit = useCallback(
    async (values: ProjectCardFormValues) => {
      const projectUpdatePayload: Partial<Project> = {
        projectStageId: Number(values.projectStage?.id),
        name: project.name,
        ownerId: values.projectManager?.id || project.ownerId,
        day1StartDate: formatProjectDate(values.day1Date) || project.day1StartDate,
        estimatedDay1Date:
          formatProjectDate(values.estimatedDay1) || project.estimatedDay1Date,
        signingDate: formatProjectDate(values.estimatedSigning) || project.signingDate,
        warrantyExpirationDate:
          formatProjectDate(values.warrantyExpiration) || project.warrantyExpirationDate,
        projectLostReasonId: values.projectLostReason?.id || project.projectLostReasonId,
        lostReasonNotes: values.lostReasonNotes || project.lostReasonNotes,
      };

      if (!projectUpdatePayload.projectStageId) {
        return;
      }

      if (!hasChanges(projectUpdatePayload, project)) {
        return;
      }

      const result = await updateProject(projectUpdatePayload);
      if (!result.errors) {
        showSuccessAlert('Project updated!');
      }
    },
    [project, showSuccessAlert, updateProject],
  );

  return (
    <Card
      sx={{
        marginTop: theme.spacing(2),
        border: '1px solid',
        borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
      }}
    >
      <StyledCardHeader
        title={
          <Stack direction="row" alignItems="center">
            <FormProvider {...form}>
              <FormEditableText
                type="editable-text"
                name="projectName"
                defaultValue={project.name || ''}
                onBlur={form.handleSubmit(onSubmitEditedProjectName)}
                textFieldProps={{
                  inputProps: {
                    maxLength: 200,
                  },
                }}
                disabled={disabled}
              />
            </FormProvider>

            <Tooltip title="Click to copy">
              <IconButton aria-label="settings" onClick={handleNameCopy}>
                <DynamicIcon name="copy" library="lucide" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
        action={
          !!menuOptions?.length &&
          !disabled && (
            <EllipsesMenu
              icon="more-vertical"
              iconLibrary="lucide"
              triggerTestId="project-card-actions-trigger"
              options={menuOptions.map((option) => ({
                label: option.label,
                icon: option.icon,
                iconLibrary: option.iconLibrary,
                testId: option.testId,
                onClick: option.handler,
              }))}
            />
          )
        }
      />
      <CardContent
        sx={{
          padding: theme.utils.important(theme.spacing(2)),
        }}
      >
        <DynamicForm
          fields={formFields}
          onSubmit={onSubmit}
          watchable
          disabled={disabled}
          sx={{ gap: 0 }}
        />
      </CardContent>
    </Card>
  );
};
